import styled from "@emotion/styled";

import andrea from "@/../../hypetrain/static/img/coach-andrea.png";
import ashley from "@/../../hypetrain/static/img/coach-ashley.png";
import brenna from "@/../../hypetrain/static/img/coach-brenna.png";
import brian from "@/../../hypetrain/static/img/coach-brian.png";
import colette from "@/../../hypetrain/static/img/coach-colette.png";
import jorge from "@/../../hypetrain/static/img/coach-jorge.png";
import marlene from "@/../../hypetrain/static/img/coach-marlene.png";
import sam from "@/../../hypetrain/static/img/coach-sam.png";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import { Carousel } from "@/components/pieces/carousel";
import { FiveStars } from "@/components/pieces/marketing/FiveStars";
import { MAINTEXT_COLOR } from "@/css/constants";

const CAROUSEL_HEIGHT = 260;

const DEFAULT_COACHES = [
  {
    photo: sam,
    name: "Sam D.",
    promo:
      "Executive career coach, HR Executive, and Talent Acquisition leader at multinational organizations.",
    accent: "#FBD1B9",
    specialtySlug: null,
    products: ["exec"],
  },
  {
    photo: andrea,
    name: "Andrea M.",
    promo:
      "Executive career coach, HR Executive, and Talent Acquisition leader at multinational organizations.",
    accent: "#FBD1B9",
    specialtySlug: null,
    products: ["placement"],
  },
  {
    photo: brian,
    name: "Brian W.",
    promo:
      "HR Executive and Talent Acquisition leader at multinational organizations.",
    accent: "#BEE7CF",
    specialtySlug: null,
    products: ["exec", "placement"],
  },
  {
    photo: marlene,
    name: "Marlene B.",
    promo:
      "25+ years of successfully advising individuals at all levels of experience.",
    accent: "#F9E7BD",
    specialtySlug: null,
    products: ["exec", "placement"],
  },
  {
    photo: brenna,
    name: "Brenna H.",
    promo:
      "Brenna brings over 10 years of experience as a professional coach trained in neuro-linguistic programming.",
    accent: "#D1D1F9",
    specialtySlug: null,
    products: ["exec", "placement"],
  },
  {
    photo: ashley,
    name: "Ashley B.",
    promo:
      "10+ years in workforce performance. Coached 1,000+ college grads and 100+ military veterans to job offers.",
    accent: "#FACEE3",
    specialtySlug: "job-search-coaching",
    products: ["exec", "placement"],
  },
  {
    photo: jorge,
    name: "Jorge B.",
    promo:
      "30+ years of global leadership experience as CEO, COO, VP in multibillion-dollar organizations.",
    accent: "#BEE7CF",
    specialtySlug: "leadership-coaching",
    products: ["exec", "placement"],
  },
  {
    photo: colette,
    name: "Colette G.",
    promo:
      "30+ years supporting professional development and success of clients and colleagues.",
    accent: "#FACEE3",
    specialtySlug: null,
    products: ["exec", "placement"],
  },
];

type Coach = (typeof DEFAULT_COACHES)[number];

function ShowcaseItem({ coach }: { coach: Coach }) {
  return (
    <StyledShowcaseItem px={[3, 40]} py={[3, 30]}>
      <Flex alignItems="center" className="full-height">
        <CoachImgWrapper mr={[4, 40]}>
          <LazyImg width="200px" src={coach.photo} className="inline-block" />
        </CoachImgWrapper>
        <Flex flexDirection="column" alignItems="flex-start" flex={1}>
          <Box className="relative" mb={[1, 2]}>
            <h2 style={{ marginTop: 0 }}>{coach.name}</h2>
            <AccentBox backgroundColor={coach.accent} />
          </Box>
          <Box color={MAINTEXT_COLOR} fontSize={[2, 20]}>
            {coach.promo}
          </Box>
          <Box mt={2}>
            <FiveStars />
          </Box>
        </Flex>
      </Flex>
    </StyledShowcaseItem>
  );
}

interface ShowcaseProps {
  specialtySlug?: string;
  header?: React.ReactNode;
}

export function CoachShowcase({ specialtySlug, header }: ShowcaseProps) {
  const domain = IS_EXEC ? "exec" : "placement";

  const coaches = DEFAULT_COACHES.filter(
    (c) =>
      (!specialtySlug ||
        !c.specialtySlug ||
        c.specialtySlug === specialtySlug) &&
      c.products.includes(domain),
  );

  const carousel = (
    <CarouselWrapper mt={[2, 5]} px={[0, 0, 0, 75]}>
      <Carousel
        options={{
          loop: true,
          centeredSlides: true,
          slidesPerView: 1,
          autoplay: {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },
        }}
      >
        {coaches.map((coach, index) => (
          <ShowcaseItem coach={coach} key={`coach-${index}`} />
        ))}
      </Carousel>
    </CarouselWrapper>
  );

  return (
    <Box my={[4, 5, 6, 80]}>
      <Container>
        {header ? (
          header
        ) : (
          <Box className="text-center">
            <h2 className="m0">Meet some of our world-class coaches</h2>
          </Box>
        )}
        <Flex
          alignItems="center"
          flexWrap="wrap"
          justifyContent="center"
          style={{ margin: "0 auto" }}
        >
          {carousel}
        </Flex>
      </Container>
    </Box>
  );
}

const CarouselWrapper = styled(Box)`
  width: 100%;
  position: relative;

  .coaches {
    background: #fff;
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    border: 1px solid #f4f1ff;
  }
`;

const StyledShowcaseItem = styled(Box)`
  height: ${CAROUSEL_HEIGHT}px;

  @media screen and (max-width: ${breakpoints.sm - 1}px) {
    h2 {
      font-size: 24px;
    }
  }
`;

const CoachImgWrapper = styled(Box)`
  height: 100%;

  @media screen and (max-width: ${breakpoints.sm - 1}px) {
    height: 100px;
  }
`;

const AccentBox = styled(Box)`
  position: absolute;
  height: 13px;
  bottom: 13px;
  width: 100%;
  z-index: -1;
`;
